import React, { useCallback, useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { listUserRoles } from '../graphql/queries';
import { setUserGroup } from '../graphql/mutations';

// What each Cognito group actually grants in this app. The pool's own group
// descriptions are inconsistent, so these win when we recognize the name.
//
// Two independent axes (2026-09-22). Vendor groups decide WHICH hardware a
// user can configure; fullConfigurator (and specialPricing, which implies it)
// decides HOW MUCH of the configurator they see. Neither grants the other. A
// user with no vendor group lands on the Configurator Home page and sees no
// hardware at all — that is where every new account starts. See
// hasVendorAccess in vendorRegistry.js.
const ROLE_DESCRIPTIONS = {
  adminAccess: 'Admin tools — audit log, this page',
  specialPricing: 'Pricing, quote generation, saved-quote visibility — implies fullConfigurator; grants no hardware on its own',
  fullConfigurator: 'Full configurator view — consultation toggle + BOM + basic pricing; grants no hardware on its own',
  aicHardware: 'AIC hardware configurator',
  dellHardware: 'Dell hardware configurator',
  smcHardware: 'Supermicro hardware configurator',
  dualCpuAccess: 'May choose the Dual CPU flash node (VCH-5500-F1N); no exec pricing',
};

const NO_ROLE = '(no roles)';

export const describeRole = (role, groups) =>
  ROLE_DESCRIPTIONS[role] ||
  groups.find((g) => g.name === role)?.description ||
  '';

const matchesSearch = (user, term) => {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return [user.name, user.email, user.username].some((field) =>
    String(field || '').toLowerCase().includes(q)
  );
};

const formatDate = (value) => (value ? String(value).slice(0, 10) : '');

// AWSJSON fields arrive as a JSON string from the API, as an object from tests.
const parseJson = (raw) => (typeof raw === 'string' ? JSON.parse(raw) : raw);

// AppSync rejections carry the Lambda's message under errors[]; anything else
// is a network-level failure with a plain message.
const errorMessage = (err) =>
  err?.errors?.[0]?.message || err?.message || 'Change failed';

/**
 * One user's roles: a toggle chip for every group the backend says is
 * editable (in allowlist order), then the user's remaining groups as
 * read-only chips. With nothing editable and no roles, the old dash.
 */
function RolesCell({ user, groups, editableGroups, pending, onToggle }) {
  const held = user.roles ?? [];
  const readOnlyRoles = held.filter((r) => !editableGroups.includes(r));
  if (!editableGroups.length && !readOnlyRoles.length) {
    return <span className="text-gray-600">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {editableGroups.map((group) => {
        const on = held.includes(group);
        const busy = pending.has(`${user.username}:${group}`);
        return (
          <button
            key={group}
            type="button"
            data-testid={`role-toggle-${user.username}-${group}`}
            title={describeRole(group, groups)}
            aria-pressed={on}
            aria-busy={busy}
            disabled={busy}
            onClick={() => onToggle(user, group)}
            className={`px-1.5 py-0.5 rounded border text-xs ${
              on
                ? 'border-orange-400/40 text-orange-300 hover:border-orange-400'
                : 'border-dashed border-gray-700 text-gray-500 hover:border-gray-500 hover:text-gray-300'
            } ${busy ? 'opacity-50 cursor-wait' : ''}`}
          >
            {group}
          </button>
        );
      })}
      {readOnlyRoles.map((role) => (
        <span
          key={role}
          title={describeRole(role, groups)}
          className="px-1.5 py-0.5 rounded border border-orange-400/40 text-orange-300 text-xs"
        >
          {role}
        </span>
      ))}
    </div>
  );
}

export function UserRolesPage() {
  const [data, setData] = useState({ users: [], groups: [], editableGroups: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  // `${username}:${group}` keys for toggles whose round trip is still running.
  const [pending, setPending] = useState(() => new Set());
  const [rowErrors, setRowErrors] = useState({});

  const load = useCallback(async () => {
    const client = generateClient();
    const res = await client.graphql({ query: listUserRoles });
    const parsed = parseJson(res?.data?.listUserRoles);
    return {
      users: parsed?.users ?? [],
      groups: parsed?.groups ?? [],
      // Which groups setUserGroup will accept. The Lambda that enforces the
      // allowlist publishes it, so a roster without it (an older backend)
      // simply renders no toggles rather than guessing.
      editableGroups: parsed?.editableGroups ?? [],
    };
  }, []);

  // Non-optimistic on purpose: the chip goes busy, and flips only from the
  // roles the server hands back, so a rejected change leaves the row as it was.
  const toggleGroup = useCallback(async (user, group) => {
    const key = `${user.username}:${group}`;
    const member = !user.roles?.includes(group);
    setPending((prev) => new Set(prev).add(key));
    setRowErrors((prev) => {
      if (!(user.username in prev)) return prev;
      const next = { ...prev };
      delete next[user.username];
      return next;
    });
    try {
      const client = generateClient();
      const res = await client.graphql({
        query: setUserGroup,
        variables: { username: user.username, group, member },
      });
      const result = parseJson(res?.data?.setUserGroup);
      const roles = Array.isArray(result?.roles) ? result.roles : user.roles;
      setData((prev) => ({
        ...prev,
        users: prev.users.map((u) => (u.username === user.username ? { ...u, roles } : u)),
      }));
    } catch (err) {
      console.error(`Failed to set ${group} for ${user.username}:`, err);
      setRowErrors((prev) => ({ ...prev, [user.username]: errorMessage(err) }));
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setData(await load());
    } catch (err) {
      console.error('Failed to load user roles:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await load();
        if (!cancelled) setData(next);
      } catch (err) {
        console.error('Failed to load user roles:', err);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [load]);

  const { users, groups, editableGroups } = data;

  // Count from the roster rather than the group list so a group that exists
  // in the pool but has no members still shows an honest zero.
  const roleCounts = groups.map((g) => ({
    name: g.name,
    count: users.filter((u) => u.roles?.includes(g.name)).length,
  }));
  const unassigned = users.filter((u) => !u.roles?.length).length;
  const disabled = users.filter((u) => !u.enabled).length;

  const shown = users
    .filter((u) => matchesSearch(u, search))
    .filter((u) => {
      if (roleFilter === 'all') return true;
      if (roleFilter === NO_ROLE) return !u.roles?.length;
      return u.roles?.includes(roleFilter);
    })
    .filter((u) => {
      if (statusFilter === 'all') return true;
      return statusFilter === 'enabled' ? u.enabled : !u.enabled;
    });

  if (loading) return <div className="p-4">Loading users...</div>;
  if (loadError) {
    return (
      <div>
        <div className="p-4 text-red-400">Failed to load users</div>
        <button
          type="button"
          onClick={refresh}
          className="ml-4 px-3 py-1 rounded border border-gray-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-baseline gap-3 mb-1">
        <h1 className="text-xl font-bold text-orange-400">Users &amp; Roles</h1>
        <button
          type="button"
          data-testid="users-refresh"
          onClick={refresh}
          className="text-xs text-gray-400 hover:text-white underline"
        >
          Refresh
        </button>
      </div>
      <p className="text-gray-400 mb-4">
        Cognito user pool roster. Roles are Cognito groups. Hardware roles, fullConfigurator and
        dualCpuAccess can be toggled here and every change is recorded in the Audit Log; adminAccess,
        specialPricing, execAccess and catalogAuthor are still changed in the Cognito console or
        with <code className="text-gray-300">scripts/add-users-to-group.sh</code>. A user picks up
        a change at their next sign-in or token refresh.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        <span data-testid="users-count-total" className="px-2 py-1 rounded border border-gray-700 bg-gray-900 text-sm">
          {users.length} users
        </span>
        {roleCounts.map((r) => (
          <button
            key={r.name}
            type="button"
            title={describeRole(r.name, groups)}
            data-testid={`users-count-${r.name}`}
            onClick={() => setRoleFilter(roleFilter === r.name ? 'all' : r.name)}
            className={`px-2 py-1 rounded border text-sm ${
              roleFilter === r.name
                ? 'border-orange-400 text-orange-400'
                : 'border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-500'
            }`}
          >
            {r.name}: {r.count}
          </button>
        ))}
        <span data-testid="users-count-unassigned" className="px-2 py-1 rounded border border-gray-700 bg-gray-900 text-sm text-gray-400">
          no roles: {unassigned}
        </span>
        <span data-testid="users-count-disabled" className="px-2 py-1 rounded border border-gray-700 bg-gray-900 text-sm text-gray-400">
          disabled: {disabled}
        </span>
      </div>

      <div className="mb-3 flex flex-wrap gap-3 items-center">
        <input
          data-testid="users-filter-search"
          placeholder="Search name, email, username..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-gray-900 text-white border border-gray-700 px-2 py-1 w-72"
        />
        <label className="text-gray-300" htmlFor="users-filter-role">Role:</label>
        <select
          id="users-filter-role"
          data-testid="users-filter-role"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="bg-gray-900 text-white border border-gray-700 px-2 py-1"
        >
          <option value="all">all</option>
          {groups.map((g) => <option key={g.name} value={g.name}>{g.name}</option>)}
          <option value={NO_ROLE}>{NO_ROLE}</option>
        </select>
        <label className="text-gray-300" htmlFor="users-filter-status">Status:</label>
        <select
          id="users-filter-status"
          data-testid="users-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-gray-900 text-white border border-gray-700 px-2 py-1"
        >
          <option value="all">all</option>
          <option value="enabled">enabled</option>
          <option value="disabled">disabled</option>
        </select>
        <span className="text-gray-500 text-sm">{shown.length} shown</span>
      </div>

      {shown.length === 0 ? (
        <div className="p-4 text-gray-400">No users match these filters</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-orange-400 text-left">
                <th className="p-2">user</th>
                <th className="p-2">email</th>
                <th className="p-2">roles</th>
                <th className="p-2">status</th>
                <th className="p-2">created</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <tr key={u.username} className="border-t border-gray-800 align-top">
                  <td className="p-2">
                    <div>{u.name || u.username}</div>
                    {u.name && <div className="text-xs text-gray-500">{u.username}</div>}
                  </td>
                  <td className="p-2">{u.email}</td>
                  <td className="p-2">
                    <RolesCell
                      user={u}
                      groups={groups}
                      editableGroups={editableGroups}
                      pending={pending}
                      onToggle={toggleGroup}
                    />
                    {rowErrors[u.username] && (
                      <div data-testid={`role-error-${u.username}`} className="text-red-400 text-xs mt-1">
                        {rowErrors[u.username]}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    <span className={u.enabled ? '' : 'text-red-400'}>
                      {u.enabled ? u.status : `${u.status} (disabled)`}
                    </span>
                  </td>
                  <td className="p-2 text-gray-400">{formatDate(u.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default UserRolesPage;
